import { FunnelChart, formatNumber, formatPercent, type ChartPoint } from '@platform/design-system';
import { usePageContext } from '../../usePageContext.ts';
import { useFunnel, type FunnelReport } from './widget-queries.ts';
import type { WidgetProps } from './widget-props.ts';
import { WidgetBody } from './WidgetBody.tsx';
import styles from './Widgets.module.css';

const FUNNEL_STEPS = ['received', 'confirmed', 'shipped', 'delivered'] as const;

const funnelStepsOf = (
  steps: FunnelReport['steps'],
  describe: { readonly locale: string; readonly label: (step: string) => string },
): readonly ChartPoint[] =>
  FUNNEL_STEPS.map((step) => ({
    key: step,
    label: describe.label(step),
    value: steps[step],
    display: formatNumber(steps[step], describe.locale),
  }));

export const FunnelWidget = ({ context }: WidgetProps<'funnel'>) => {
  const { t, locale } = usePageContext();
  const funnel = useFunnel(context);
  const report = funnel.data;
  return (
    <WidgetBody query={funnel}>
      <FunnelChart
        label={t('dashboard.widgets.kind.funnel')}
        steps={
          report === undefined
            ? []
            : funnelStepsOf(report.steps, {
                locale,
                label: (step) => t(`dashboard.widgets.funnelStep.${step}`),
              })
        }
        describeStep={({ ofFirst, fromPrevious }) =>
          fromPrevious === null
            ? t('dashboard.widgets.funnelFirst')
            : t('dashboard.widgets.funnelShare', {
                ofFirst: formatPercent(ofFirst, locale),
                fromPrevious: formatPercent(fromPrevious, locale),
              })
        }
        emptyTitle={t('dashboard.noOrdersTitle')}
        loading={funnel.isPending}
      />
      {report === undefined || report.steps.returned === 0 ? null : (
        <p className={styles['note']}>
          {t('dashboard.widgets.funnelReturned', {
            count: report.steps.returned,
            total: formatNumber(report.steps.returned, locale),
          })}
        </p>
      )}
    </WidgetBody>
  );
};
